import { useEffect, useRef } from 'react';

/**
 * Custom hook to lock body scroll when a modal/lightbox is open.
 * Handles iOS Safari rubber banding and preserves scroll position.
 *
 * @param isLocked - Whether scroll should be locked
 */
export function useBodyScrollLock(isLocked: boolean) {
  const scrollY = useRef(0);

  useEffect(() => {
    if (!isLocked) return;

    // Save current scroll position
    scrollY.current = window.scrollY;

    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Apply scroll lock styles
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY.current}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // Prevent touch scroll on background (iOS Safari fix)
    const preventTouchMove = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Allow touch events inside lightbox content
      if (target.closest('[data-lightbox-content]')) return;
      e.preventDefault();
    };

    document.addEventListener('touchmove', preventTouchMove, { passive: false });

    // Cleanup function
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.paddingRight = '';

      // Restore scroll position
      window.scrollTo(0, scrollY.current);

      document.removeEventListener('touchmove', preventTouchMove);
    };
  }, [isLocked]);
}
